import React, { useState, Fragment, useEffect, useRef } from "react";
import { Table, Row, Col, Card, CardBody, Spinner } from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { showToast } from "../ToastNotification";
import CommonLoading from "./CommonLoading";
import { getCurrentFiscalYearDates } from "./WorkingDateRange";
import { useVirtualizer } from '@tanstack/react-virtual';

const TableContainerDateRangeFilter2 = ({
  footer,
  type,
  summary,
  columns,
  data,
  theadClass = "",
  tbodyClass = "",
  onDateRangeChange,
  dateRange,
  setDateRange,
  globalFilter,
  setGlobalFilter
}) => {
  
  const [summaryData, setSummarydata] = useState(summary);
  const [sorting, setSorting] = useState([]);
  const { fyStart, fyEnd } = getCurrentFiscalYearDates();
  
  const [tableLoading, setTableLoading] = useState(false);

  const convertToDateFormat = (dateString) => {
    const months = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    };

    const [day, month, year] = dateString.replace(",", "").split(" ");
    return `${year}-${months[month]}-${day.padStart(2, "0")}`;
  };

  // console.log("summary", summary);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState,
  } = table;

  // const initialRange =
  //   summary?.start && summary?.end
  //     ? [convertToDateFormat(summary?.start), convertToDateFormat(summary?.end)]
  //     : [];

  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [day, month, year] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day); // Month is zero-based in JS Date
  };
  
  const initialRange =
    summary?.start && summary?.end
      ? [parseDate(summary.start), parseDate(summary.end)]
      : [];
  

  // console.log("initialRange",dateRange, initialRange, summaryData);

  const handleApplyClick = () => {
    // console.log("dateRange", dateRange,summary);
    const filteredArr = dateRange.filter((item) => item !== null);
    const formatDate = (date) => {
      return `${date?.getFullYear()}-${String(date?.getMonth() + 1).padStart(2, "0")}-${String(date?.getDate()).padStart(2, "0")}`;
    };
    function convertToYMD(dateStr) {
      const [day, month, year] = dateStr.split("-");
      return `${year}-${month}-${day}`;
  }
    if (filteredArr?.length > 1) {
      setTableLoading(true);
      if (onDateRangeChange) {
        // const formatDate = (date) => {
        //   return `${date?.getFullYear()}-${String(date?.getMonth() + 1).padStart(2, "0")}-${String(date?.getDate()).padStart(2, "0")}`;
        // };

        onDateRangeChange({
          start_date: formatDate(dateRange[0]),
          end_date: formatDate(dateRange[1]),
          setTableLoading,
        });
      }
    } else {
      setTableLoading(true);
      onDateRangeChange({
        start_date: convertToYMD(summary?.start),
        end_date: convertToYMD(summary?.end),
        setTableLoading,
      });
      // showToast("Please select a date range to filter", "error");
    }
  };

  const tableContainerRef = useRef(null);
  
  const rows = table.getRowModel().rows;

  // Virtual row configuration
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 45, // Estimated row height in pixels
    overscan: 10, // Number of items to render outside visible area
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom = virtualRows.length > 0 
    ? totalSize - virtualRows[virtualRows.length - 1].end 
    : 0;


  useEffect(() => {
    if(type!=="FO") {
      const totals = getRowModel()?.rows.reduce(
        (acc, item) => {
          acc.total_buy_qty += +item?.original.buy_qty || 0;
          acc.total_buy_value += +item?.original.buy_value || 0;
          acc.total_sell_qty += +item?.original.sell_qty || 0;
          acc.total_sell_value += +item?.original.sell_value || 0;
          acc.total_net_qty += +item?.original.net_qty || 0;
          acc.total_net_value += +item?.original.net_value || 0;
          acc.total_pnl += +item?.original.pnl || 0;
  
          return acc;
        },
        {
          total_buy_qty: 0,
          total_buy_value: 0,
          total_sell_qty: 0,
          total_sell_value: 0,
          total_net_qty: 0,
          total_net_value: 0,
          total_pnl: 0,
          start: summary?.start,
          end: summary?.end,
        }
      );
      
      setSummarydata(totals);
    }
   
  }, [getRowModel, globalFilter,tableLoading]);
  const isNumeric = (value) => {
    if (typeof value === "number") return true;
    if (typeof value === "string") {
      // Remove commas (if any) before checking.
      return !isNaN(Number(value.replace(/,/g, "")));
    }
    return false;
  };

  return (
    <Fragment>
      {/* Search and Date Filter */}
      <Row className="">
      {/* <div className="search-box d-inline-block col-xxl-3 col-xl-4 col-lg-7 col-12 mb-2">

          <input
            id="search-bar-0"
            type="text"
            style={{
              backgroundColor: "#F1F4F7",
              border: "1px solid transparent",
            }}
            className="form-control border-0 search"
            placeholder="Search by instrument"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
          <i className="bx bx-search-alt search-icon"></i>
        </div> */}
        {/* <div className="date-box d-inline-block col-xxl-4 col-xl-4 col-lg-8 col-8">
          <Flatpickr
            style={{
              backgroundColor: "#F1F4F7",
              border: "1px solid transparent",
            }}
            options={{
              mode: "range",
              dateFormat: "d-m-Y",
              minDate: fyStart,
              maxDate: new Date(Math.min(fyEnd, new Date(new Date().setDate(new Date().getDate() - 1)))),
              onClose: (selectedDates, dateStr, instance) => {
                if (selectedDates.length === 2) {
                  const diffInDays =
                    (selectedDates[1] - selectedDates[0]) /
                    (1000 * 60 * 60 * 24);
                  if (diffInDays > 89) {
                    instance.clear(); // Clear selection if the range exceeds 7 days
                    // alert("You can only select a maximum range of 7 days.");
                    showToast(
                      "You can only select a maximum range of 90 days.",
                      "error"
                    );
                  }
                }
              },
            }}
            value={dateRange.length ? dateRange : initialRange}
            onChange={setDateRange}
            className="form-control"
          />
          <i className="bx bx-calendar date-icon"></i>
        </div>
        <div className="col-xxl-2 col-xl-2 col-lg-4 col-4">
          <button className="btn btn-primary w-100" onClick={handleApplyClick}>
            Apply
          </button>
        </div> */}
      </Row>

      {/* Table Container */}
      <div className="table-responsive table-container">
      <div
        ref={tableContainerRef}
        style={{
          maxHeight: '70vh', 
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <table className="table table-hover align-middle table-nowrap table-bordered">
        <thead 
            className={`bg-gl-primary sticky-header ${theadClass}`}
            style={{
              position: 'sticky',
              top: -1,
              zIndex: 2,
              background: 'inherit'
            }}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      style={{
                        textAlign: header.column.columnDef.headerTextAlign,
                        cursor: "pointer",
                        whiteSpace: "normal",
                        padding: "0.3rem",
                        paddingLeft: "0.4rem"
                      }}
                      // onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        {flexRender(
                          header.column.columnDef.Header,
                          header.getContext()
                        )}
                        {/* {isSorted === "asc" ? (
                          <i className="ms-2 bx bx-caret-up"></i>
                        ) : isSorted === "desc" ? (
                          <i className="ms-2 bx bx-caret-down"></i>
                        ) : (
                          <i className="ms-2 bx bxs-sort-alt"></i>
                        )} */}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          {tableLoading ? (
            <tbody>
            <tr>
              <td
                colSpan={columns?.length}
                className="p-1"
                style={{ textAlign: "center" }}
              >
                <div className="card m-0 text-muted">
                  <div className="card-body centerContent">
                    {CommonLoading && (
                      <CommonLoading
                        style={{
                          maxHeight: "6vh",
                        }}
                      />
                    )}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
          ) : (
            <>
              <tbody className={tbodyClass}>
                {data?.length > 0 ? (
                  rows?.length > 0 ? (
                    <>
                      {paddingTop > 0 && (
                        <tr>
                          <td colSpan={columns?.length} style={{ height: `${paddingTop}px`, padding: 0 }} />
                        </tr>
                      )}
                      {virtualRows.map((virtualRow) => {
                        const row = rows[virtualRow.index];
                        return (
                          <tr
                            key={row.id}
                            style={
                              row.original.isGroupSummary
                                ? {
                                    backgroundColor: "#F1F4F7",
                                    borderColor: "#F1F4F7",
                                  }
                                : {}
                            }
                            className={
                              row.original.isGroupSummary ? "group-summary" : ""
                            }
                          >
            {row.getVisibleCells().map((cell) => {
              // Get the raw cell value.
              const rawValue = cell.getValue();
              // Render the cell using its defined cell renderer.
              let renderedContent = flexRender(
                cell.column.columnDef.cell,
                cell.getContext()
              );

              // For all cells except "pnl", if the raw value is numeric, re-format it.
              if (cell.column.id !== "pnl" && isNumeric(rawValue)) {
                const numericValue = Number(String(rawValue).replace(/,/g, ""));
                renderedContent = numericValue.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              } else if (
                cell.column.id !== "pnl" &&
                !React.isValidElement(renderedContent) &&
                isNumeric(rawValue)
              ) {
                // If the rendered content isn't a React element (e.g. a plain string/number), then format it.
                const numericValue = Number(String(rawValue).replace(/,/g, ""));
                renderedContent = numericValue.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              }
              // For "pnl", the custom renderer (with its color and any icons) remains unchanged.

              return (
                <td
                  key={cell.id}
                  className="fs-p-bg fw-medium"
                  style={{
                    textAlign: cell.column.columnDef.bodyTextAlign,
                    whiteSpace: "normal",
                    padding: "0.3rem",
                    paddingLeft: "0.4rem"
                  }}
                >
                  {renderedContent}
                </td>
              );
            })}
           </tr>
                        );
                      })}
                      {paddingBottom > 0 && (
                        <tr>
                          <td colSpan={columns?.length} style={{ height: `${paddingBottom}px`, padding: 0 }} />
                        </tr>
                      )}
                    </>
                  ) : (
                    <tr>
                      <td
                        colSpan={columns?.length}
                        className="p-1"
                        style={{ textAlign: "center" }}
                      >
                        <Card className="m-0 text-muted">
                          <CardBody>No Record Found</CardBody>
                        </Card>
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td
                      colSpan={columns?.length}
                      className="p-1"
                      style={{ textAlign: "center" }}
                    >
                      <Card className="m-0 text-muted">
                        <CardBody>No Record Found</CardBody>
                      </Card>
                    </td>
                  </tr>
                )}
              </tbody>
              {footer && type==="FO" && !globalFilter && (
                <tfoot className="table-footer sticky-footer">
                  <tr className="bg-gl-success">
                    <th  style={{ textAlign: "start" }}>TOTAL</th>
                    <th colSpan={3} style={{ textAlign: "end" }}></th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_buy_qty ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_buy_value ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_sell_qty ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_sell_value ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_net_qty ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_net_value ?? 0
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {/* {Number(summaryData?.total_sell_value ?? 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })} */}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {/* {Number(summaryData?.total_net_qty ?? 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                      })} */}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(summaryData?.total_pnl ?? 0).toLocaleString(
                        "en-IN", {
                          minimumFractionDigits: 2,
        maximumFractionDigits: 2,
                        }
                      )}
                    </th>
                  </tr>
                </tfoot>
              )}
               {footer && type==="CAP" && (
                <tfoot className="table-footer sticky-footer">
                  <tr className="bg-gl-success">
                    <th colSpan={1} style={{ textAlign: "start" }}>TOTAL</th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_buy_qty ?? 0
                      ).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_buy_value ?? 0
                      ).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_sell_qty ?? 0
                      ).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_sell_value ?? 0
                      ).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_net_qty ?? 0
                      ).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_net_value ?? 0
                      ).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {/* {Number(summaryData?.total_sell_value ?? 0).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })} */}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {/* {Number(summaryData?.total_net_qty ?? 0).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })} */}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(summaryData?.total_pnl ?? 0).toLocaleString(
                        "en-IN", {
                           minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                        }
                      )}
                    </th>
                  </tr>
                </tfoot>
              )}
               {footer && type==="COM" && (
                <tfoot className="table-footer sticky-footer">
                  <tr className="bg-gl-success">
                    <th colSpan={1} style={{ textAlign: "start" }}>TOTAL</th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_buy_qty ?? 0
                      ).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_buy_value ?? 0
                      ).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      
                    </th>
                    <th style={{ textAlign: "end" }}>
                      
                    </th>
                    <th style={{ textAlign: "end" }}>
                      
                      </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_sell_qty ?? 0
                      ).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_sell_value ?? 0
                      ).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_net_qty ?? 0
                      ).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(
                        summaryData?.total_net_value ?? 0
                      ).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {/* {Number(summaryData?.total_sell_value ?? 0).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })} */}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {/* {Number(summaryData?.total_net_qty ?? 0).toLocaleString("en-IN", {
                         minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                      })} */}
                    </th>
                    <th style={{ textAlign: "end" }}>
                      {Number(summaryData?.total_pnl ?? 0).toLocaleString(
                        "en-IN", {
                           minimumFractionDigits: 2,
                       maximumFractionDigits: 2,
                        }
                      )}
                    </th>
                  </tr>
                </tfoot>
              )}
            </>
          )}
        </table>
        </div>
    </div>
    </Fragment>
  );
};

export default TableContainerDateRangeFilter2; 

// import React, { useState, Fragment, useEffect, useRef } from "react";
// import { Table, Row, Col, Card, CardBody, Spinner } from "reactstrap";
// import Flatpickr from "react-flatpickr";
// import "flatpickr/dist/themes/material_blue.css";
// import {
//   useReactTable,
//   getCoreRowModel,
//   getSortedRowModel,
//   getFilteredRowModel,
//   flexRender,
// } from "@tanstack/react-table";
// import { FixedSizeList as List } from "react-window";
// import { showToast } from "../ToastNotification";

// const TableContainerDateRangeFilter2 = ({
//   footer,
//   type,
//   summary,
//   columns,
//   data,
//   theadClass = "",
//   tbodyClass = "",
//   onDateRangeChange,
// }) => {
//   const [globalFilter, setGlobalFilter] = useState("");
//   const [summaryData, setSummarydata] = useState(summary);
//   const [sorting, setSorting] = useState([]);
//   const [dateRange, setDateRange] = useState([]);
//   const [tableLoading, setTableLoading] = useState(false);
//   const [tableWidth, setTableWidth] = useState(0);
//   const [columnWidths, setColumnWidths] = useState([]);
//   const tableContainerRef = useRef(null);
//   const tableRef = useRef(null);

//   console.log("columnWidths",columnWidths)
  
//   // Calculate row height based on content - adjust as needed
//   const ROW_HEIGHT = 50;

//   const parseDate = (dateString) => {
//     if (!dateString) return null;
//     const [day, month, year] = dateString.split("-").map(Number);
//     return new Date(year, month - 1, day); // Month is zero-based in JS Date
//   };
  
//   const initialRange =
//     summary?.start && summary?.end
//       ? [parseDate(summary.start), parseDate(summary.end)]
//       : [];

//   const table = useReactTable({
//     columns,
//     data,
//     getCoreRowModel: getCoreRowModel(),
//     getSortedRowModel: getSortedRowModel(),
//     getFilteredRowModel: getFilteredRowModel(),
//     state: {
//       globalFilter,
//       sorting,
//     },
//     onGlobalFilterChange: setGlobalFilter,
//     onSortingChange: setSorting,
//   });

//   // Calculate column widths and total table width
//   useEffect(() => {
//     if (tableRef.current) {
//       // Get all column widths - use exact pixel values
//       const colWidths = columns.map((col) => col.minWidth || 150);
//       setColumnWidths(colWidths);
//       const calculatedWidth = colWidths.reduce((sum, width) => sum + width, 0);
//       setTableWidth(calculatedWidth);
//     }
//   }, [columns]);

//   const handleApplyClick = () => {
//     const filteredArr = dateRange.filter((item) => item !== null);
//     if (filteredArr?.length > 1) {
//       setTableLoading(true);
//       if (onDateRangeChange) {
//         const formatDate = (date) => {
//           return `${date?.getFullYear()}-${String(date?.getMonth() + 1).padStart(2, "0")}-${String(date?.getDate()).padStart(2, "0")}`;
//         };

//         onDateRangeChange({
//           start_date: formatDate(dateRange[0]),
//           end_date: formatDate(dateRange[1]),
//           setTableLoading,
//         });
//       }
//     } else {
//       showToast("Please select a date range to filter", "error");
//     }
//   };

//   useEffect(() => {
//     const totals = table.getRowModel()?.rows.reduce(
//       (acc, item) => {
//         acc.total_buy_qty += +item?.original.buy_qty || 0;
//         acc.total_buy_value += +item?.original.buy_value || 0;
//         acc.total_sell_qty += +item?.original.sell_qty || 0;
//         acc.total_sell_value += +item?.original.sell_value || 0;
//         acc.total_net_qty += +item?.original.net_qty || 0;
//         acc.total_net_value += +item?.original.net_value || 0;
//         acc.total_pnl += +item?.original.pnl || 0;

//         return acc;
//       },
//       {
//         total_buy_qty: 0,
//         total_buy_value: 0,
//         total_sell_qty: 0,
//         total_sell_value: 0,
//         total_net_qty: 0,
//         total_net_value: 0,
//         total_pnl: 0,
//         start: summary?.start,
//         end: summary?.end,
//       }
//     );
//     setSummarydata(totals);
//   }, [table.getRowModel, globalFilter, tableLoading, summary]);

//   // Common cell styling function to ensure consistency
//   const getCellStyle = (index, isHeader = false) => {
//     return {
//       width: columnWidths[index] || 150,
//       textAlign: isHeader ? 
//         (columns[index]?.columnDef?.headerTextAlign || "left") : 
//         (columns[index]?.columnDef?.bodyTextAlign || "left"),
//       padding: isHeader ? "12px 8px" : "8px",
//       overflow: "hidden",
//       textOverflow: "ellipsis",
//       whiteSpace: "nowrap",
//       borderRight: "1px solid #e9e9ef",
//       ...(isHeader ? { fontWeight: "bold" } : {})
//     };
//   };

//   // Calculate visible height for the list
//   const getListHeight = () => {
//     const rows = table?.getRowModel()?.rows;
//     const calculatedHeight = rows.length * ROW_HEIGHT;
//     // Limit the height to show a maximum of 10 rows (or adjust as needed)
//     return Math.min(calculatedHeight, 10 * ROW_HEIGHT);
//   };

//   return (
//     <Fragment>
//       {/* Search and Date Filter */}
//       <div className="mb-2 row">
//         <div className="search-box d-inline-block col-xxl-6 col-xl-6 col-lg-12 col-12 mb-xxl-0 mb-lg-2 mb-2">
//           <input
//             id="search-bar-0"
//             type="text"
//             style={{
//               backgroundColor: "#F1F4F7",
//               border: "1px solid transparent",
//             }}
//             className="form-control border-0 search"
//             placeholder="Search..."
//             value={globalFilter}
//             onChange={(e) => setGlobalFilter(e.target.value)}
//           />
//           <i className="bx bx-search-alt search-icon"></i>
//         </div>
//         <div className="date-box d-inline-block col-xxl-4 col-xl-4 col-lg-8 col-8">
//           <Flatpickr
//             style={{
//               backgroundColor: "#F1F4F7",
//               border: "1px solid transparent",
//             }}
//             options={{ mode: "range", dateFormat: "d-m-Y", maxDate: "today" }}
//             value={dateRange.length ? dateRange : initialRange}
//             onChange={setDateRange}
//             className="form-control"
//           />
//           <i className="bx bx-calendar date-icon"></i>
//         </div>
//         <div className="col-xxl-2 col-xl-2 col-lg-4 col-4">
//           <button className="btn btn-primary w-100" onClick={handleApplyClick}>
//             Apply
//           </button>
//         </div>
//       </div>

//       {/* Table Container */}
//       <div 
//         className="table-responsive table-container" 
//         style={{ 
//           overflowX: "auto",
//           position: "relative",
//           border: "1px solid #e9e9ef",
//           borderRadius: "0.25rem"
//         }} 
//         ref={tableContainerRef}
//       >
//         <div 
//           className="table align-middle table-nowrap table-bordered"
//           ref={tableRef}
//           style={{ 
//             width: tableWidth || "100%", 
//             minWidth: "100%",
//             position: "relative",
//             borderCollapse: "collapse"
//           }}
//         >
//           {/* Table Header */}
//           <div 
//             className={`bg-gl-primary sticky-header ${theadClass}`}
//             style={{ 
//               display: "flex", 
//               width: tableWidth || "100%",
//               position: "sticky",
//               top: 0,
//               zIndex: 1
//             }}
//           >
//             {table.getHeaderGroups().map((headerGroup) => (
//               <div 
//                 key={headerGroup.id} 
//                 style={{ 
//                   display: "flex", 
//                   width: "100%" 
//                 }}
//               >
//                 {headerGroup.headers.map((header, index) => (
//                   <div
//                     key={header.id}
//                     className="th"
//                     style={getCellStyle(index, true)}
//                   >
//                     <div className="d-flex align-items-center justify-content-between">
//                       {flexRender(
//                         header.column.columnDef.Header,
//                         header.getContext()
//                       )}
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             ))}
//           </div>

//           {/* Table Body */}
//           {tableLoading ? (
//             <div style={{ textAlign: "center", padding: "20px", width: "100%" }}>
//               <Card className="m-0 text-muted">
//                 <CardBody className="centerContent">
//                   <Spinner color="primary" />
//                 </CardBody>
//               </Card>
//             </div>
//           ) : (
//             <div className={`tbody ${tbodyClass}`} style={{ width: "100%" }}>
//               {table.getRowModel().rows.length > 0 ? (
//                 <List
//                   height={getListHeight()}
//                   itemCount={table.getRowModel().rows.length}
//                   itemSize={ROW_HEIGHT}
//                   width="100%"
//                   style={{ overflowX: "hidden" }}
//                 >
//                   {({ index: rowIndex, style }) => {
//                     const row = table.getRowModel().rows[rowIndex];
//                     if (!row) return null;
                    
//                     return (
//                       <div 
//                         className="tr" 
//                         role="row" 
//                         style={{ 
//                           ...style, 
//                           display: "flex", 
//                           width: "100%",
//                           borderBottom: "1px solid #e9e9ef",
//                           backgroundColor: rowIndex % 2 === 0 ? "#fff" : "#f8f9fa"
//                         }}
//                       >
//                         {row.getVisibleCells().map((cell, cellIndex) => (
//                           <div
//                             key={cell.id}
//                             className="td fs-p-bg fw-medium"
//                             style={{
//                               ...getCellStyle(cellIndex, false),
//                               display: "flex",
//                               alignItems: "center"
//                             }}
//                           >
//                             {flexRender(cell.column.columnDef.cell, cell.getContext())}
//                           </div>
//                         ))}
//                       </div>
//                     );
//                   }}
//                 </List>
//               ) : (
//                 <div style={{ width: "100%" }}>
//                   <Card className="m-0 text-muted">
//                     <CardBody>No Record Found</CardBody>
//                   </Card>
//                 </div>
//               )}
//             </div>
//           )}

//           {/* Table Footer */}
//           {footer && !tableLoading && (
//             <div 
//               className="table-footer sticky-footer bg-gl-success"
//               style={{ 
//                 display: "flex", 
//                 width: tableWidth || "100%",
//                 position: "sticky",
//                 bottom: 0,
//                 zIndex: 1,
//                 borderTop: "1px solid #e9e9ef"
//               }}
//             >
//               {type === "FO" && (
//                 <>
//                   <div style={{ 
//                     width: (columnWidths.slice(0, 4).reduce((acc, width) => acc + width, 0)), 
//                     textAlign: "end", 
//                     padding: "8px", 
//                     fontWeight: "bold",
//                     borderRight: "1px solid #e9e9ef"
//                   }}>Total</div>
//                   {[
//                     { key: "total_buy_qty", colIndex: 4 },
//                     { key: "total_buy_value", colIndex: 5 },
//                     { key: "total_sell_qty", colIndex: 6 },
//                     { key: "total_sell_value", colIndex: 7 },
//                     { key: "total_net_qty", colIndex: 8 },
//                     { key: "total_net_value", colIndex: 9 },
//                     { key: "", colIndex: 10 },
//                     { key: "", colIndex: 11 },
//                     { key: "total_pnl", colIndex: 12 }
//                   ].map((item, idx) => (
//                     <div 
//                       key={idx}
//                       style={{ 
//                         width: columnWidths[item.colIndex] || 150, 
//                         textAlign: "end", 
//                         padding: "8px", 
//                         fontWeight: "bold",
//                         borderRight: idx < 8 ? "1px solid #e9e9ef" : "none"
//                       }}
//                     >
//                       {item.key ? parseFloat(summaryData?.[item.key] ?? 0).toLocaleString("en-IN") : ""}
//                     </div>
//                   ))}
//                 </>
//               )}
//               {type === "CAP" && (
//                 <>
//                   <div style={{ 
//                     width: columnWidths[0] || 150, 
//                     textAlign: "end", 
//                     padding: "8px", 
//                     fontWeight: "bold",
//                     borderRight: "1px solid #e9e9ef"
//                   }}>Total</div>
//                   {[
//                     { key: "total_buy_qty", colIndex: 1 },
//                     { key: "total_buy_value", colIndex: 2 },
//                     { key: "total_sell_qty", colIndex: 3 },
//                     { key: "total_sell_value", colIndex: 4 },
//                     { key: "total_net_qty", colIndex: 5 },
//                     { key: "total_net_value", colIndex: 6 },
//                     { key: "", colIndex: 7 },
//                     { key: "", colIndex: 8 },
//                     { key: "total_pnl", colIndex: 9 }
//                   ].map((item, idx) => (
//                     <div 
//                       key={idx}
//                       style={{ 
//                         width: columnWidths[item.colIndex] || 150, 
//                         textAlign: "end", 
//                         padding: "8px", 
//                         fontWeight: "bold",
//                         borderRight: idx < 8 ? "1px solid #e9e9ef" : "none"
//                       }}
//                     >
//                       {item.key ? parseFloat(summaryData?.[item.key] ?? 0).toLocaleString("en-IN") : ""}
//                     </div>
//                   ))}
//                 </>
//               )}
//             </div>
//           )}
//         </div>
//       </div>
//     </Fragment>
//   );
// };

// export default TableContainerDateRangeFilter2;